import { groupLinkedParticipants } from '@citizen-walk/shared-types';
import type { MembershipProfileValue, ParticipantSummary } from '@citizen-walk/shared-types';

/**
 * ユーザー管理の一覧行を作る純粋ヘルパー (2026-09-07)。
 *
 * `listParticipants` は **端末単位** (membership doc 単位) で返す。端末の引き継ぎ・復帰をした
 * 参加者は同じ人が複数行になるため、そのまま並べると「参加者が実際より多く見える」「同じ人を
 * 二重に編集する」ことになる。ここで `linkedFromUids` の連結成分 = 1 人にまとめ、
 * 画面は常に **人単位** で扱う (サーバの配分 assignmentEngine と同じ規則)。
 */

export interface ParticipantPersonRow {
  /** 代表 uid (最終アクセスが最新の端末)。修正・再発行はこの uid に対して行う。 */
  uid: string;
  /** この人の全 uid (代表を含む)。 */
  allUids: string[];
  nickname: string;
  /** 参加者情報の専用欄 (項目の型に応じて文字列 / 配列 / 数値 / 真偽値)。 */
  gender: MembershipProfileValue;
  ageGroup: MembershipProfileValue;
  relationship: MembershipProfileValue;
  attribute: MembershipProfileValue;
  profileFieldValues: Record<string, unknown>;
  /** 全端末の参加セッションの和集合。 */
  joinedSessionIds: string[];
  /** 現在の対象セッション (代表端末の値。未設定なら他端末の値を拾う)。 */
  mainSessionId: string | null;
  /** 端末数 (2 以上 = 引き継ぎ・復帰をした人)。 */
  deviceCount: number;
  /** 最終アクセス (全端末で最も新しいもの、ISO8601)。 */
  lastSeenAt: string | null;
  /** 登録日時 (全端末で最も古いもの、ISO8601)。 */
  createdAt: string | null;
  /** 引き継ぎコードを設定済みか (いずれかの端末で設定されていれば true)。 */
  hasRecoveryCode: boolean;
}

function maxIso(a: string | null, b: string | null | undefined): string | null {
  if (!b) return a;
  if (!a) return b;
  return b > a ? b : a;
}

function minIso(a: string | null, b: string | null | undefined): string | null {
  if (!b) return a;
  if (!a) return b;
  return b < a ? b : a;
}

/** 端末単位の一覧を人単位に畳む。並びは元の一覧順 (callable 側でニックネーム順)。 */
export function toPersonRows(participants: readonly ParticipantSummary[]): ParticipantPersonRow[] {
  const rows = participants.map((p) => ({
    ...p,
    linkedFromUids: p.linkedFromUids ?? [],
    lastSeenAtMs: p.lastSeenAt ? Date.parse(p.lastSeenAt) || null : null,
  }));
  return groupLinkedParticipants(rows).map((g) => {
    const sessions = new Set<string>();
    let lastSeenAt: string | null = null;
    let createdAt: string | null = null;
    let hasRecoveryCode = false;
    let mainSessionId: string | null = g.primary.mainSessionId ?? null;
    const profileFieldValues: Record<string, unknown> = {};
    for (const row of g.rows) {
      for (const sid of row.joinedSessionIds) sessions.add(sid);
      lastSeenAt = maxIso(lastSeenAt, row.lastSeenAt);
      createdAt = minIso(createdAt, row.createdAt);
      if (row.recoveryCodeSetAt) hasRecoveryCode = true;
      if (!mainSessionId && row.mainSessionId) mainSessionId = row.mainSessionId;
      // 追加の質問への回答は端末ごとにずれうる。代表端末の値を最優先し、欠けている項目だけ補う。
      for (const [key, value] of Object.entries(row.profileFieldValues ?? {})) {
        if (!(key in profileFieldValues)) profileFieldValues[key] = value;
      }
    }
    for (const [key, value] of Object.entries(g.primary.profileFieldValues ?? {})) {
      profileFieldValues[key] = value;
    }
    return {
      uid: g.primaryUid,
      allUids: g.uids,
      nickname: g.primary.nickname,
      gender: g.primary.gender,
      ageGroup: g.primary.ageGroup,
      relationship: g.primary.relationship,
      attribute: g.primary.attribute,
      profileFieldValues,
      joinedSessionIds: [...sessions],
      mainSessionId,
      deviceCount: g.rows.length,
      lastSeenAt,
      createdAt,
      hasRecoveryCode,
    };
  });
}

/**
 * 一覧の絞り込み (pure)。`keyword` はニックネーム / ユーザーID の部分一致 (大文字小文字を無視)、
 * `sessionId` は「そのセッションに参加している人」。どちらも空なら素通し。
 */
export function filterPersonRows(
  rows: readonly ParticipantPersonRow[],
  keyword: string,
  sessionId: string,
): ParticipantPersonRow[] {
  const kw = keyword.trim().toLowerCase();
  return rows.filter((r) => {
    if (sessionId === '__none__') {
      if (r.joinedSessionIds.length > 0) return false;
    } else if (sessionId && !r.joinedSessionIds.includes(sessionId)) {
      return false;
    }
    if (!kw) return true;
    return (
      r.nickname.toLowerCase().includes(kw) || r.allUids.some((u) => u.toLowerCase().includes(kw))
    );
  });
}
